import React, { useCallback, useContext, useState, useEffect } from 'react'
import MarkerInterface from '../../interfaces/Marker'

import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { mapOptions } from '../../config-map'
import Distance from '../Distance/Distance'
import { calcIntervalDistance, calcTravelledDistance, mountInformation } from '../../utils'
import TagTitle from '../TagTitle/TagTitle'
import { DashboardContext } from 'live/context/dashboard'
import EmptyContent from '../EmptyContent/EmptyContent'
import './map-polyline.scss'
import { getApiMapKey } from '../../api/google-integration'
import Map from '../Map/Map'

declare const google

interface IProps {
    markers: MarkerInterface[]
    zoom: number
    steps: number
    width: number
}

let travelledDistance = 0
let intervalDistance: any[] = []
let polylines: any = null

const MapPolyline = ({ markers = [], zoom, steps, width }: IProps): JSX.Element => {
    const [markersPin, setMarkersPin] = useState<any[]>([])
    const [center, setCenter] = useState<any>()
    const [information, setInformation] = useState<any>()
    const [markersActive, setMarkersActive] = useState<MarkerInterface[]>()

    const context: any = useContext(DashboardContext)
    const { filterValues } = context?.state || {}
    const [mapInstance, setMapInstance] = useState<any>()

    const onLoad = useCallback(map => {
        setMapInstance(map)
    }, [])

    useEffect(() => {
        createMarkersPin(markers)
        renderPolylines(markers)

        intervalDistance = calcIntervalDistance(markers)
        travelledDistance = calcTravelledDistance(markers)
        setMarkersActive([])

        if (markers.length > 0) {
            mountInformation(markers[0], 0, 0).then(result => {
                setInformation(result)
            })
        } else {
            setInformation({})
        }
    }, [markers, mapInstance])

    useEffect(() => {
        setCenter({ lat: information?.lat, lng: information?.lng })
    }, [information])

    useEffect(() => {
        setHighlightPolyline(markersActive)
    }, [markersActive])

    const createMarkersPin = (markers: MarkerInterface[]) => {
        const last = markers.length > 0 ? markers[markers.length - 1] : markers[0]

        if (last !== null) {
            const firstMarker: MarkerInterface = markers[0]
            const lastMarker: MarkerInterface = last

            const pins = [
                {
                    position: { lat: firstMarker?.lat, lng: firstMarker?.lng },
                    icon: {
                        path:
                            'M23.25 10.5H20.8594C20.2031 6.75 17.25 3.79688 13.5 3.14062V0.75C13.5 0.375 13.125 0 12.75 0H11.25C10.8281 0 10.5 0.375 10.5 0.75V3.14062C6.70312 3.79688 3.75 6.75 3.09375 10.5H0.75C0.328125 10.5 0 10.875 0 11.25V12.75C0 13.1719 0.328125 13.5 0.75 13.5H3.09375C3.75 17.2969 6.70312 20.25 10.5 20.9062V23.25C10.5 23.6719 10.8281 24 11.25 24H12.75C13.125 24 13.5 23.6719 13.5 23.25V20.9062C17.25 20.25 20.2031 17.2969 20.8594 13.5H23.25C23.625 13.5 24 13.1719 24 12.75V11.25C24 10.875 23.625 10.5 23.25 10.5ZM12 18C8.67188 18 6 15.3281 6 12C6 8.71875 8.67188 6 12 6C15.2812 6 18 8.71875 18 12C18 15.3281 15.2812 18 12 18ZM12 7.875C9.70312 7.875 7.875 9.75 7.875 12C7.875 14.2969 9.70312 16.125 12 16.125C14.25 16.125 16.125 14.2969 16.125 12C16.125 9.75 14.25 7.875 12 7.875Z',
                        strokeColor: '#606A80',
                        scale: 1,
                        fillOpacity: 0.8
                    }
                },
                {
                    position: { lat: lastMarker?.lat, lng: lastMarker?.lng },
                    icon: {
                        path:
                            'M11.3906 9.90625V13.0938C12.6094 13.375 13.6875 13.8438 14.8125 14.1719V10.9844C13.5938 10.7031 12.5156 10.2344 11.3906 9.90625ZM21.8438 4.14062C20.25 4.89062 18.2812 5.64062 16.3594 5.64062C13.875 5.64062 11.7656 4 8.625 4C7.45312 4 6.42188 4.23438 5.4375 4.5625C5.57812 4.23438 5.625 3.85938 5.57812 3.48438C5.53125 2.125 4.40625 1.09375 3.09375 1C1.59375 0.953125 0.375 2.17188 0.375 3.625C0.375 4.51562 0.796875 5.3125 1.5 5.78125V23.875C1.5 24.5312 1.96875 25 2.625 25H3.375C3.98438 25 4.5 24.5312 4.5 23.875V19.4688C5.8125 18.9062 7.45312 18.4375 9.84375 18.4375C12.375 18.4375 14.4375 20.0781 17.5781 20.0781C19.8281 20.0781 21.6562 19.2812 23.3438 18.1562C23.7188 17.875 23.9531 17.4062 23.9531 16.8906V5.5C24 4.42188 22.8281 3.71875 21.8438 4.14062ZM7.92188 16.2812C6.70312 16.4219 5.57812 16.6562 4.5 17.0781V13.75C5.71875 13.3281 6.70312 13.0469 7.92188 12.9531V16.2812ZM21.75 9.95312C20.625 10.4219 19.5469 10.8906 18.2812 11.0781V14.4062C19.4531 14.2656 20.6719 13.8906 21.75 13.1875V16.5156C20.5312 17.2656 19.4531 17.6875 18.2812 17.7812V14.4062C17.0156 14.5938 16.0312 14.5 14.8125 14.1719V17.3125C13.6875 16.9844 12.6094 16.5625 11.3906 16.3281V13.0938C10.4531 12.9062 9.46875 12.8125 7.92188 12.9531V9.67188C6.89062 9.8125 5.85938 10.1406 4.5 10.6562V7.32812C6.04688 6.76562 6.84375 6.39062 7.92188 6.29688V9.67188C9.1875 9.48438 10.2188 9.57812 11.3906 9.90625V6.76562C12.4688 7.09375 13.5938 7.5625 14.8125 7.75V10.9844C15.9375 11.2188 17.0625 11.3125 18.2812 11.0781V7.70312C19.5469 7.51562 20.7188 7.09375 21.75 6.67188V9.95312Z',
                        strokeColor: '#606A80',
                        scale: 1,
                        fillOpacity: 0.8
                    }
                }
            ]

            setMarkersPin(pins)
        }
    }

    const _handleDraggableOnStop = async (event, ui, width) => {
        const lastXKm = (ui.lastX * travelledDistance) / width

        const travelledTime = markers[markers.length - 1].time - markers[0].time

        const element = intervalDistance.find(ele => lastXKm >= ele.start && lastXKm <= ele.end)

        const indexMarker = markers.findIndex(ele => ele.id === element?.marker?.id)

        clearHighlightPolyline(markersActive)

        if (indexMarker !== -1) {
            const marker = markers[indexMarker]

            const markersActive: MarkerInterface[] = []

            markersActive.push(marker)

            for (let index = indexMarker + 1; index < markers.length - 1; index++) {
                if (markers[index]?.levelColor !== marker?.levelColor) {
                    break
                }

                markersActive.push(markers[index])
            }

            for (let index = indexMarker - 1; index >= 0; index--) {
                if (markers[index]?.levelColor !== marker?.levelColor) {
                    break
                }

                markersActive.push(markers[index])
            }

            setMarkersActive(markersActive)

            const lastXTime = (ui.lastX * travelledTime) / width

            if (element?.marker) {
                const result = await mountInformation(element.marker, lastXTime, lastXKm)
                setInformation(result)
            }
        }
    }

    const renderPins = () => {
        return (
            <>
                {markersPin &&
                    markersPin.map(({ position, icon }: any, index: number) => {
                        const { lat, lng } = position

                        if (!lat || !lng) return

                        return <Marker key={index} position={position} icon={icon} />
                    })}
            </>
        )
    }

    const renderPolylines = markers => {
        if (mapInstance && markers) {
            clearPolylines()

            polylines = {}

            for (let index = 1; index < markers.length; index++) {
                const previous = markers[index - 1]
                const current = markers[index]

                const { lat: previousLat, lng: previousLng } = previous || { lat: undefined, lng: undefined }
                const { lat, id, lng, levelColor } = current

                const ruleA = lat !== undefined && lng !== undefined
                const ruleB = previousLat !== undefined && previousLng !== undefined

                if (ruleA && ruleB) {
                    const polyline = new google.maps.Polyline({
                        path: [
                            { lat, lng },
                            { lat: previousLat, lng: previousLng }
                        ],
                        strokeColor: levelColor || '#000000',
                        strokeOpacity: 1,
                        strokeWeight: 8,
                        visible: true,
                        zIndex: 1,
                        map: mapInstance
                    })

                    polylines[id] = polyline
                }
            }
        }
    }

    const setHighlightPolyline = markersActive => {
        if (markersActive) {
            markersActive.forEach(({ id, levelColor }: MarkerInterface) => {
                const options = {
                    strokeColor: levelColor || '#000000',
                    strokeOpacity: 1,
                    strokeWeight: 16,
                    visible: true,
                    zIndex: 2
                }

                if (polylines[id]) {
                    polylines[id].setOptions(options)
                }
            })
        }
    }

    const clearHighlightPolyline = markersActive => {
        if (markersActive) {
            markersActive.forEach(({ id, levelColor }: MarkerInterface) => {
                const options = {
                    strokeColor: levelColor || '#000000',
                    strokeOpacity: 1,
                    strokeWeight: 8,
                    visible: true,
                    zIndex: 1
                }

                if (polylines[id]) {
                    polylines[id].setOptions(options)
                }
            })
        }
    }

    const clearPolylines = () => {
        if (polylines !== null) {
            Object.keys(polylines).forEach(key => {
                polylines[key].setMap(null)
            })
        }

        polylines = null
    }

    return (
        <>
            {markers.length === 0 && <EmptyContent />}

            {filterValues && filterValues.comfort_tranquility && (
                <TagTitle title={filterValues.comfort_tranquility[0]} />
            )}

            <Distance
                markers={markers}
                steps={steps}
                _handleDraggableOnStop={_handleDraggableOnStop}
                width={width}
                information={information}
            />

            <Map
                zoom={zoom}
                center={center}
                onLoad={onLoad}
                options={mapOptions}
                mapContainerClassName="map-markers-container"
            >
                {renderPins()}
            </Map>
        </>
    )
}

export default MapPolyline
